import { Element } from "@xmpp/xml"
import { useCallback, useContext, useState } from "react"
import { XmppContext } from "@/stream/xmppClient"
import AdHocCommands from "@/api/adHocCommands"
import adHocCommands, { CarRouteConfig } from "@/api/adHocCommands"
import { OrderAnalyzeRoute } from "@/lib/orderAnalyzeRouteParser"
import XmppPubSubApi from "@/api/xmppPubSubApi"
import { RouteStop } from "@/reducers/routes2gSlice"
import {
  BillingMetaData,
  OrderMetaData,
  StopMetaData,
} from "@/components/maps/AnalyzeOrderVerifyStops"
import Route2gFromFormParser from "@/lib/route2gFromFormParser"
import { useEventCallback } from "@/hooks/use-event-callback"
import { useAppSelector } from "@/reducers/hooks"
import { selectActiveChat } from "@/reducers/chatsSliceSelectors"
import { selectRouteJidFromChatJid } from "@/reducers/selectors"
import PlanRouteParser from "@/lib/planRouteParser"
import { selectLocation } from "@/reducers/locationsSelectors"
import { handleFetchPerLegAlternatives } from "@/hooks/useTemporaryRoute"
import { parseRouteErrors } from "@/lib/routeErrors"

export type UploadedFile = {
  url: string
  name: string
  file?: File
}

// Utility functions
const trackAnalyzeOrderError = (
  error: unknown,
  activeChat: string | undefined,
  messageId?: string,
) => {
  window.analytics.track("AnalyzeOrderFailed", {
    reason: "xmpp_processing_error",
    error: error instanceof Error ? error.message : "Unknown error",
    activeChat,
    stack: error instanceof Error ? error.stack : undefined,
    messageId: messageId,
  })
}

// "true"/"false" from the assistant's <meta>; anything else is "not stated".
const parseMetaBoolean = (text: string | undefined): boolean | undefined => {
  const value = text?.trim().toLowerCase()
  if (value === "true") return true
  if (value === "false") return false
  return undefined
}

const parseMetaNumber = (text: string | undefined): number | undefined => {
  const value = text?.trim().replace(",", ".")
  if (!value) return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

export const parseRouteMetaData = (
  response: Element | undefined,
): OrderMetaData | null => {
  const routeMetaElement = response?.getChild("meta", "urn:slickshift:route:0")
  if (!routeMetaElement) return null

  const billing = parseBillingMetaData(routeMetaElement)

  return {
    ref_number: routeMetaElement
      .getChildren("ref-number")
      .map((el) => el.getText())
      .join(", "),
    notes: routeMetaElement
      .getChildren("notes")
      .map((el) => el.getText())
      .join(", "),
    pallet_exchange_notes:
      routeMetaElement.getChildText("pallet-exchange-notes") || undefined,
    pallet_exchange: parseMetaBoolean(
      routeMetaElement.getChildText("pallet-exchange") ?? undefined,
    ),
    temperature_min: parseMetaNumber(
      routeMetaElement.getChildText("temperature-min") ?? undefined,
    ),
    temperature_max: parseMetaNumber(
      routeMetaElement.getChildText("temperature-max") ?? undefined,
    ),
    adr_info: routeMetaElement.getChildText("adr-info") || undefined,
    weight: routeMetaElement
      .getChildren("weight")
      .map((el) => `${el.getText()} kg`)
      .join(", "),
    price_netto_eur: routeMetaElement
      .getChildren("price-netto-eur")
      .map((el) => el.getText())
      .map((price) => Number(price))
      .reduce((acc, curr) => acc + curr, 0),
    billing,
    nip:
      routeMetaElement.getChildText("nip")?.replace(/[,;\s]+$/, "") ||
      undefined,
    payment_deadline:
      routeMetaElement.getChildText("payment-deadline") || undefined,
    order_number: routeMetaElement.getChildText("order-number") || undefined,
    transport_number:
      routeMetaElement.getChildText("transport-number") || undefined,
    customer_name: routeMetaElement.getChildText("customer-name") || undefined,
    customer_address:
      routeMetaElement.getChildText("customer-address") || undefined,
    customer_postal_code:
      routeMetaElement.getChildText("customer-postal-code") || undefined,
    customer_contact:
      routeMetaElement.getChildText("customer-contact") || undefined,
    customer_email:
      routeMetaElement.getChildText("customer-email") || undefined,
    customer_phone:
      routeMetaElement.getChildText("customer-phone") || undefined,
    recipient_name:
      routeMetaElement.getChildText("recipient-name") || undefined,
    recipient_nip: routeMetaElement.getChildText("recipient-nip") || undefined,
    dispatch_location:
      routeMetaElement.getChildText("dispatch-location") || undefined,
    driver_name: routeMetaElement.getChildText("driver-name") || undefined,
    car_plates: routeMetaElement.getChildText("car-plates") || undefined,
    trailer_plates:
      routeMetaElement.getChildText("trailer-plates") || undefined,
    coordinator_name:
      routeMetaElement.getChildText("coordinator-name") || undefined,
    empty_kilometers:
      routeMetaElement.getChildText("empty-kilometers") || undefined,
    loaded_kilometers:
      routeMetaElement.getChildText("loaded-kilometers") || undefined,
  }
}

const parseBillingMetaData = (
  routeMetaElement: Element,
): BillingMetaData | undefined => {
  const billingElements = routeMetaElement.getChildren("billing")
  if (billingElements.length === 0) return undefined

  const getBillingText = (fieldName: string) => {
    const values = billingElements
      .flatMap((billingElement) => billingElement.getChildren(fieldName))
      .map((el) => el.getText())
      .map((text) => text?.trim() || "")
      .filter(Boolean)

    return values.length > 0 ? values.join("\n") : undefined
  }

  const getBillingBoolean = (fieldName: string) => {
    const values = billingElements
      .flatMap((billingElement) => billingElement.getChildren(fieldName))
      .map((el) => el.getText()?.trim().toLowerCase())
      .filter(Boolean)

    if (values.includes("true")) return true
    if (values.includes("false")) return false
    return undefined
  }

  const getBillingNumber = (fieldName: string) => {
    const value = getBillingText(fieldName)
    if (!value) return undefined

    const parsed = Number.parseInt(value, 10)
    return Number.isNaN(parsed) ? undefined : parsed
  }

  const billing: BillingMetaData = {
    original_transport_documents_required: getBillingBoolean(
      "original-transport-documents-required",
    ),
    original_transport_documents_notes: getBillingText(
      "original-transport-documents-notes",
    ),
    digital_transport_documents_notes: getBillingText(
      "digital-transport-documents-notes",
    ),
    payment_terms_days: getBillingNumber("payment-terms-days"),
    payment_terms_notes: getBillingText("payment-terms-notes"),
    client_platform_upload_required: getBillingBoolean(
      "client-platform-upload-required",
    ),
    client_platform_upload_notes: getBillingText(
      "client-platform-upload-notes",
    ),
    important_settlement_notes: getBillingText("important-settlement-notes"),
  }

  const hasBillingData = Object.values(billing).some(
    (value) => value !== undefined && value !== "",
  )

  return hasBillingData ? billing : undefined
}

const createRouteStopsFromMetaData = (
  extractedStops: StopMetaData[],
  routeMetaData: OrderMetaData,
): RouteStop[] => {
  return extractedStops.map((metaDataStop) => {
    const scheduledTimeMax = metaDataStop.scheduled_hour_max || "23:59:00"
    const scheduledDate = metaDataStop.scheduled_arrival_time?.split("T")[0]
    const scheduled_arrival_time = scheduledDate
      ? `${scheduledDate}T${scheduledTimeMax}`
      : undefined

    return {
      action: metaDataStop.action,
      location: {
        formattedAddress:
          metaDataStop.place?.formattedAddress ??
          metaDataStop.location?.formattedAddress,
        companyName: metaDataStop.location?.companyName,
        postalCode: metaDataStop.place?.addressComponents
          ? (metaDataStop.place.addressComponents.find((c) =>
              c.types.includes("postal_code"),
            )?.longText ?? metaDataStop.location?.postalCode)
          : metaDataStop.location?.postalCode,
        // Preserve the structured address + coordinates so the Marcos order
        // receives real street/city/country fields instead of an empty
        // station. These come straight from the parsed stop.
        street: metaDataStop.location?.street,
        locality: metaDataStop.location?.locality,
        country: metaDataStop.location?.country,
        countryCode: metaDataStop.location?.countryCode,
        lat: metaDataStop.location?.lat,
        lon: metaDataStop.location?.lon,
      },
      addressUpdated: false,
      action_reference_number: metaDataStop.action_reference_number,
      ref: routeMetaData.order_number,
      scheduled_hour_min: metaDataStop.scheduled_hour_min,
      scheduled_hour_max: metaDataStop.scheduled_hour_max,
      time_fixed: metaDataStop.time_fixed,
      pallet_count: metaDataStop.pallet_count,
      weight_kg: metaDataStop.weight_kg,
      scheduled_arrival_time,
    } as RouteStop
  })
}

type ProcessingState = {
  isProcessing: boolean
  isRouteDetected: boolean
  isFailed: boolean
  isFetchingPolylines: boolean
  isFetchingPolylinesFailed: boolean
}

type RouteState = {
  routeData: OrderAnalyzeRoute | undefined
  stops: RouteStop[] | undefined
  routeMetaData: OrderMetaData | undefined
  carConfig: CarRouteConfig | undefined
}

export const useExtractDataFromOrder = ({
  fetchOnlyMetaData,
  messageId,
}: {
  fetchOnlyMetaData: boolean
  messageId?: string
}) => {
  const [processingState, setProcessingState] = useState<ProcessingState>({
    isProcessing: false,
    isRouteDetected: false,
    isFailed: false,
    isFetchingPolylines: false,
    isFetchingPolylinesFailed: false,
  })

  const [routeState, setRouteState] = useState<RouteState>({
    routeData: undefined,
    stops: undefined,
    routeMetaData: undefined,
    carConfig: undefined,
  })

  const [polylineErrors, setPolylineErrors] = useState<string[] | null>(null)
  const clearPolylineErrors = useCallback(() => setPolylineErrors(null), [])

  const { client } = useContext(XmppContext)
  const chat = useAppSelector(selectActiveChat)
  const routeJid = useAppSelector(selectRouteJidFromChatJid(chat.jid))
  const currentLocation = useAppSelector(selectLocation(routeJid))

  const resetExtractingState = useCallback(() => {
    setProcessingState({
      isProcessing: false,
      isRouteDetected: false,
      isFailed: false,
      isFetchingPolylines: false,
      isFetchingPolylinesFailed: false,
    })
    setPolylineErrors(null)
    setRouteState((prev) => ({
      ...prev,
      routeData: undefined,
    }))
  }, [])

  const fetchPolylines = useEventCallback(
    async (
      stops: RouteStop[],
      routeMetaData: OrderMetaData,
      initialCarConfig?: CarRouteConfig,
    ) => {
      if (!routeJid || !currentLocation) return

      setRouteState((prev) => ({ ...prev, stops }))
      setProcessingState((prev) => ({ ...prev, isFetchingPolylines: true }))

      try {
        const stopsWithCurrentLocation = [
          {
            addressUpdated: true,
            location: {
              formattedAddress: `${currentLocation.lat},${currentLocation.lon}`,
            },
          },
          ...stops,
        ]

        const result = await AdHocCommands.getRoutePlan(
          client,
          routeJid,
          stopsWithCurrentLocation,
          initialCarConfig || routeState.carConfig || {},
        )
        const routeData = PlanRouteParser.parse(result) // Fetch per-leg alternatives after main route is set

        const perLegAlternatives = await handleFetchPerLegAlternatives(
          stopsWithCurrentLocation,
          routeJid,
          {
            carConfig: initialCarConfig || routeState.carConfig || {},
            loadType: initialCarConfig?.loadType || [],
            avoid: initialCarConfig?.avoid || [],
          },
        )

        // Add route-meta data if available
        if (routeData && routeMetaData) {
          routeData.routeMetaData = routeMetaData
          routeData.perLegAlternatives = perLegAlternatives
        }

        setRouteState((prev) => ({
          ...prev,
          routeMetaData,
          routeData,
        }))
        setProcessingState((prev) => ({ ...prev, isRouteDetected: true }))
      } catch (error) {
        trackAnalyzeOrderError(error, routeJid, messageId)
        setProcessingState((prev) => ({
          ...prev,
          isFetchingPolylinesFailed: true,
          isFailed: true,
          isProcessing: false,
        }))
        setPolylineErrors(parseRouteErrors(error))
        console.error("Error processing assistant message:", error)
        throw error
      } finally {
        setProcessingState((prev) => ({ ...prev, isFetchingPolylines: false }))
      }
    },
  )

  const startProcessing = useEventCallback(
    async (uploadedFiles: UploadedFile[]) => {
      if (!client || !routeJid) return

      setProcessingState((prev) => ({
        ...prev,
        isProcessing: true,
        isFailed: false,
        isFetchingPolylines: false,
      }))

      try {
        // Fetch car configuration
        const carConfig =
          (await XmppPubSubApi.fetchCarConfig(client, routeJid)) || {}
        setRouteState((prev) => ({ ...prev, carConfig }))

        // Extract route from orders
        const result = await adHocCommands.extractRouteFromOrders(
          client,
          routeJid,
          uploadedFiles,
        )

        const response = result?.getChild("command")
        const routeMetaData = parseRouteMetaData(response)
        if (routeMetaData && response) {
          if (fetchOnlyMetaData) {
            // Handle metadata-only case
            setRouteState((prev) => ({
              ...prev,
              routeMetaData,
              stops: Route2gFromFormParser.parse(response)?.stops,
            }))
            setProcessingState((prev) => ({ ...prev, isRouteDetected: true }))
          } else {
            // Handle full processing case
            const route2g = Route2gFromFormParser.parse(response)
            if (route2g) {
              // Extract stops without IDs (new stops)
              const extractedStops =
                (route2g.stops?.filter(
                  (stop) => !stop.id,
                ) as unknown as StopMetaData[]) || []

              // Filter existing stops with IDs
              const existingStops = route2g.stops.filter(
                (stop: RouteStop) => stop.id,
              )

              // Create new RouteStop objects from metadata
              const newStops = createRouteStopsFromMetaData(
                extractedStops,
                routeMetaData,
              )

              // Fetch polylines for all stops with car configuration and route metadata

              await fetchPolylines(
                [...existingStops, ...newStops],
                {
                  ...routeMetaData,
                  stops: extractedStops,
                },
                carConfig,
              )

              window.analytics.track("AnalyzeOrderSuccess", {
                activeChat: routeJid,
                stopsCount: route2g.stops.length,
                hasRouteMetaData: true,
              })
            }
          }
        }
      } catch (error) {
        trackAnalyzeOrderError(error, routeJid, messageId)
        setProcessingState((prev) => ({
          ...prev,
          isFailed: true,
          isProcessing: false,
          isFetchingPolylines: false,
        }))
      }
    },
  )

  const stopProcessing = useCallback(() => {
    setProcessingState((prev) => ({ ...prev, isProcessing: false }))
  }, [])

  const setCarConfig = useCallback((carConfig: CarRouteConfig | undefined) => {
    setRouteState((prev) => ({ ...prev, carConfig }))
  }, [])

  const setRouteData = useCallback(
    (routeData: OrderAnalyzeRoute | undefined) => {
      setRouteState((prev) => ({ ...prev, routeData }))
    },
    [],
  )

  // Same end state as the metadata-only branch of startProcessing (routeMetaData
  // + stops set, isRouteDetected=true) but for callers that already have the
  // metadata in hand — no document to upload or extract, e.g. a standstill
  // order the dispatcher fills in manually.
  const setManualOrder = useCallback(
    (metaData: OrderMetaData, stops: RouteStop[] = []) => {
      setRouteState((prev) => ({ ...prev, routeMetaData: metaData, stops }))
      setProcessingState((prev) => ({
        ...prev,
        isProcessing: false,
        isFailed: false,
        isRouteDetected: true,
      }))
    },
    [],
  )

  return {
    // Processing state
    isProcessing: processingState.isProcessing,
    isRouteDetected: processingState.isRouteDetected,
    isFailed: processingState.isFailed,
    isFetchingPolylines: processingState.isFetchingPolylines,
    isFetchingPolylinesFailed: processingState.isFetchingPolylinesFailed,

    // Route state
    carConfig: routeState.carConfig,
    stops: routeState.stops,
    routeData: routeState.routeData,
    routeMetaData: routeState.routeMetaData,

    // Actions
    setCarConfig,
    setRouteData,
    startProcessing,
    setManualOrder,
    stopProcessing,
    fetchPolylines,
    resetExtractingState,
    polylineErrors,
    clearPolylineErrors,
  }
}
